package com.example.positionahead.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.example.positionahead.model.AssetClass

import com.example.positionahead.model.SavedPortfolio

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddPositionScreen(
    existingSymbols: List<String>,
    portfolios: List<SavedPortfolio>,
    activePortfolioId: String,
    onAddPurchase: (
        symbol: String,
        shares: Double,
        costBasis: Double,
        assetClass: AssetClass,
        purchaseDate: String?,
        note: String?,
        portfolioId: String?,
    ) -> Unit,
    onDone: () -> Unit,
    initialSymbol: String? = null,
    embedded: Boolean = false,
    modifier: Modifier = Modifier,
) {
    val prefill = initialSymbol?.trim()?.uppercase().orEmpty()
    val prefillExisting = prefill.isNotBlank() && prefill in existingSymbols
    
    var selectedPortfolioId by remember { mutableStateOf(activePortfolioId) }
    var portfolioExpanded by remember { mutableStateOf(false) }

    var addToExisting by remember(prefill) {
        mutableStateOf(
            when {
                prefill.isNotBlank() && !prefillExisting -> false
                prefillExisting -> true
                else -> existingSymbols.isNotEmpty()
            },
        )
    }
    var selectedSymbol by remember(prefill) {
        mutableStateOf(
            when {
                prefillExisting -> prefill
                else -> existingSymbols.firstOrNull().orEmpty()
            },
        )
    }
    var symbol by remember(prefill) { mutableStateOf(if (prefillExisting) "" else prefill) }
    var shares by remember { mutableStateOf("") }
    var costBasis by remember { mutableStateOf("") }
    var purchaseDate by remember { mutableStateOf("") }
    var note by remember { mutableStateOf("") }
    var assetClass by remember { mutableStateOf(AssetClass.STOCK) }
    var symbolExpanded by remember { mutableStateOf(false) }
    var assetExpanded by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    Column(modifier = modifier.fillMaxSize()) {
        if (!embedded) {
            TopAppBar(title = { Text("Add Purchase") })
        }

        Column(
            modifier = Modifier
                .then(if (embedded) Modifier.fillMaxWidth() else Modifier.fillMaxSize())
                .then(if (embedded) Modifier else Modifier.verticalScroll(rememberScrollState()))
                .padding(if (embedded) 0.dp else 16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            if (!embedded) {
                Text(
                    "Add a new purchase lot — for reinvestments or additional buys of an existing holding.",
                    style = MaterialTheme.typography.bodyMedium,
                )
            } else {
                Text("Add purchase", style = MaterialTheme.typography.titleMedium)
            }

            // Portfolio Selector
            ExposedDropdownMenuBox(
                expanded = portfolioExpanded,
                onExpandedChange = { portfolioExpanded = it },
            ) {
                OutlinedTextField(
                    value = portfolios.find { it.id == selectedPortfolioId }?.name ?: "Select Portfolio",
                    onValueChange = {},
                    readOnly = true,
                    label = { Text("Add to Portfolio") },
                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(portfolioExpanded) },
                    modifier = Modifier
                        .fillMaxWidth()
                        .menuAnchor(),
                )
                ExposedDropdownMenu(
                    expanded = portfolioExpanded,
                    onDismissRequest = { portfolioExpanded = false },
                ) {
                    portfolios.forEach { p ->
                        DropdownMenuItem(
                            text = { Text(p.name) },
                            onClick = {
                                selectedPortfolioId = p.id
                                portfolioExpanded = false
                            },
                        )
                    }
                }
            }

            if (existingSymbols.isNotEmpty()) {
                ExposedDropdownMenuBox(
                    expanded = symbolExpanded,
                    onExpandedChange = { symbolExpanded = it },
                ) {
                    OutlinedTextField(
                        value = if (addToExisting) "Add to existing: $selectedSymbol" else "New symbol",
                        onValueChange = {},
                        readOnly = true,
                        label = { Text("Purchase type") },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(symbolExpanded) },
                        modifier = Modifier
                            .fillMaxWidth()
                            .menuAnchor(),
                    )
                    ExposedDropdownMenu(
                        expanded = symbolExpanded,
                        onDismissRequest = { symbolExpanded = false },
                    ) {
                        DropdownMenuItem(
                            text = { Text("New symbol") },
                            onClick = {
                                addToExisting = false
                                symbolExpanded = false
                            },
                        )
                        existingSymbols.forEach { existing ->
                            DropdownMenuItem(
                                text = { Text("Add lot to $existing") },
                                onClick = {
                                    addToExisting = true
                                    selectedSymbol = existing
                                    symbolExpanded = false
                                },
                            )
                        }
                    }
                }
            }

            if (!addToExisting) {
                OutlinedTextField(
                    value = symbol,
                    onValueChange = { symbol = it.uppercase() },
                    label = { Text("Symbol") },
                    placeholder = { Text("AAPL") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                )
                ExposedDropdownMenuBox(
                    expanded = assetExpanded,
                    onExpandedChange = { assetExpanded = it },
                ) {
                    OutlinedTextField(
                        value = assetClass.name,
                        onValueChange = {},
                        readOnly = true,
                        label = { Text("Asset Class") },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(assetExpanded) },
                        modifier = Modifier
                            .fillMaxWidth()
                            .menuAnchor(),
                    )
                    ExposedDropdownMenu(
                        expanded = assetExpanded,
                        onDismissRequest = { assetExpanded = false },
                    ) {
                        AssetClass.entries.forEach { option ->
                            DropdownMenuItem(
                                text = { Text(option.name) },
                                onClick = {
                                    assetClass = option
                                    assetExpanded = false
                                },
                            )
                        }
                    }
                }
            }

            OutlinedTextField(
                value = shares,
                onValueChange = { shares = it },
                label = { Text("Shares") },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
            )
            OutlinedTextField(
                value = costBasis,
                onValueChange = { costBasis = it },
                label = { Text("Purchase price (per share)") },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
            )
            OutlinedTextField(
                value = purchaseDate,
                onValueChange = { purchaseDate = it },
                label = { Text("Purchase date (optional)") },
                placeholder = { Text("8/1/2026") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
            )
            OutlinedTextField(
                value = note,
                onValueChange = { note = it },
                label = { Text("Note (optional)") },
                placeholder = { Text("DRIP reinvest") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
            )

            error?.let {
                Text(it, color = MaterialTheme.colorScheme.error)
            }

            Button(
                onClick = {
                    val sharesValue = shares.toDoubleOrNull()
                    val costValue = costBasis.toDoubleOrNull()
                    val targetSymbol = if (addToExisting) selectedSymbol else symbol
                    when {
                        targetSymbol.isBlank() -> error = "Enter or select a symbol"
                        sharesValue == null || sharesValue <= 0 -> error = "Enter valid shares"
                        costValue == null || costValue < 0 -> error = "Enter valid purchase price"
                        else -> {
                            error = null
                            onAddPurchase(
                                targetSymbol,
                                sharesValue,
                                costValue,
                                assetClass,
                                purchaseDate.takeIf { it.isNotBlank() },
                                note.takeIf { it.isNotBlank() },
                                selectedPortfolioId,
                            )
                            shares = ""
                            costBasis = ""
                            purchaseDate = ""
                            note = ""
                            if (!addToExisting) symbol = ""
                            onDone()
                        }
                    }
                },
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(if (addToExisting) "Add Lot to $selectedSymbol" else "Add Position")
            }
        }
    }
}
